import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { PaperClipOutlined } from '@ant-design/icons';
import { Button, Checkbox, Image, Radio } from 'antd';
import { useNavigate } from 'react-router';

import type { ISchoolMainData } from '@/api/endpoints/schools';
import { DataSection } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { getRussianPluralForm } from '@/shared/helpers';

import { IconSelectLabel } from '../_components/IconSelectLabel/IconSelectLabel';
import { SCHOOL_FORMAT_RADIO_GROUP_DATA } from '../consts';
import { UnpublishSchoolModal } from '../SchoolsCatalogPage/_components/UnpublishSchoolModal/UnpublishSchoolModal';

import { SubjectionSection } from './_components/SubjectionSection/SubjectionSection';
import styles from './SchoolViewPage.module.scss';

interface IProps {
  schoolData: Omit<ISchoolMainData, 'lms_account_id'> & { lmsAccountName: string };
}

export const ViewPublicSchoolComponent = ({ schoolData }: IProps) => {
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isUnpublishSchoolModalOpened, setIsUnpublishSchoolModalOpened] = useState(false);

  const navigate = useNavigate();

  const {
    name: schoolName,
    logo: schoolLogo,
    short_description: schoolShortDescription,
    description: schoolDescription,
    icon: schoolIcon,
    duration: schoolDuration,
    lmsAccountName,
    format: schoolFormat,
    format_description: formatDescription,
    certification: hasCertificate,
    cities,
    poll,
    test,
    active: isActive,
    id: schoolId,
    abilities: schoolAbilities,
    speakers: schoolSpeakers,
    program_sections: schoolProgramSections,
    faqs: schoolFAQ,
  } = schoolData;

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`);
  };

  useLayoutEffect(() => {
    if (!isActive) {
      navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.EDIT}`);
    }
  }, [isActive, schoolId]);

  const openUnpublishSchoolModal = useCallback(() => setIsUnpublishSchoolModalOpened(true), []);
  const closeUnpublishSchoolModal = useCallback(() => setIsUnpublishSchoolModalOpened(false), []);

  const schoolFAQColumnsData = useMemo(
    () =>
      schoolFAQ && schoolFAQ.length !== 0
        ? schoolFAQ?.map(({ question, answer }) => ({
            firstColumnData: question,
            secondColumnData: answer,
          }))
        : null,
    [schoolFAQ],
  );

  const schoolProgramSectionsColumnsData = useMemo(
    () =>
      schoolProgramSections && schoolProgramSections.length !== 0
        ? schoolProgramSections.map(({ name, description }) => ({
            firstColumnData: name,
            secondColumnData: description,
          }))
        : null,
    [schoolProgramSections],
  );

  const schoolSpeakersColumnsData = useMemo(
    () =>
      schoolSpeakers && schoolSpeakers.length !== 0
        ? schoolSpeakers.map(({ full_name: fullName, bio, photo }) => ({
            firstColumnData: fullName,
            secondColumnData: bio,
            thirdColumnData: photo,
          }))
        : null,
    [schoolSpeakers],
  );

  const schoolAbilitiesColumnsData = useMemo(
    () =>
      schoolAbilities && schoolAbilities.length !== 0
        ? schoolAbilities.map(({ title, description }) => ({
            firstColumnData: title,
            secondColumnData: description,
          }))
        : null,
    [schoolAbilities],
  );

  return (
    <>
      <DataSection titleText="Название">
        <p className={styles['data-value']}>{schoolName}</p>
      </DataSection>
      <DataSection titleText="Аккаунт школы в LMS">
        <p className={styles['data-value']}>{lmsAccountName}</p>
      </DataSection>
      <DataSection titleText="Изображение школы">
        {schoolLogo && (
          <div>
            <Button
              className={styles['image-preview-button']}
              size="small"
              onClick={() => setIsImagePreviewOpen(true)}
              type="text"
              icon={<PaperClipOutlined />}
            >
              {schoolLogo.filename}
            </Button>
            <Image
              alt="Фото закрепленное за школой"
              style={{ display: 'none' }}
              preview={{
                open: isImagePreviewOpen,
                src: schoolLogo.url,
                onOpenChange: (value) => setIsImagePreviewOpen(value),
              }}
            />
          </div>
        )}
      </DataSection>
      <DataSection titleText="Иконка школы">
        <IconSelectLabel
          textClassName={styles['data-value']}
          iconType={schoolIcon}
        />
      </DataSection>
      <DataSection titleText="Краткое описание школы">
        {schoolShortDescription &&
          schoolShortDescription.length !== 0 &&
          schoolShortDescription.map((description) => (
            <p
              className={styles['data-value']}
              key={description}
            >
              {description}
            </p>
          ))}
      </DataSection>
      <DataSection titleText="Полное описание школы">
        {schoolDescription && <p className={styles['data-value']}>{schoolDescription}</p>}
      </DataSection>
      <DataSection titleText="Длительность школы">
        {schoolDuration && (
          <p className={styles['data-value']}>{getRussianPluralForm(schoolDuration, ['неделя', 'недели', 'недель'])}</p>
        )}
      </DataSection>
      <DataSection titleText="Сертификат">
        <Checkbox
          checked={hasCertificate}
          disabled
        >
          Показывать блок “Сертификат” на странице школы
        </Checkbox>
      </DataSection>
      <DataSection titleText="Города школы">
        {cities && cities.length !== 0 && (
          <p className={styles['data-value']}>{cities.map((city) => city.name).join(', ')}</p>
        )}
      </DataSection>
      <DataSection titleText="Опрос">{poll && <p className={styles['data-value']}>{poll.title}</p>}</DataSection>
      <DataSection titleText="Тестовое задание">
        {test && <p className={styles['data-value']}>{test.title}</p>}
      </DataSection>
      <DataSection titleText="Формат">
        <Radio.Group
          className={styles['format-wrapper']}
          optionType="default"
          disabled
          value={schoolFormat}
        >
          {SCHOOL_FORMAT_RADIO_GROUP_DATA.map(({ id, data: { format, buttonText, inputTitle } }) => (
            <div
              key={id}
              className={styles['format-radio-button-wrapper']}
            >
              <Radio.Button value={format}>{buttonText}</Radio.Button>
              <DataSection titleText={inputTitle}>
                <p className={styles['data-value']}>{format === schoolFormat ? formatDescription : ''}</p>
              </DataSection>
            </div>
          ))}
        </Radio.Group>
      </DataSection>
      <DataSection titleText="Вопросы и ответы">
        <SubjectionSection
          columnTitles={['Вопрос', 'Ответ на вопрос']}
          columnsData={schoolFAQColumnsData}
        />
      </DataSection>
      <DataSection titleText="Программа школы">
        <SubjectionSection
          columnTitles={['Наименование раздела', 'Описание раздела']}
          columnsData={schoolProgramSectionsColumnsData}
        />
      </DataSection>
      <DataSection titleText="Спикеры">
        <SubjectionSection
          columnTitles={['ФИО', 'Описание', 'Фото']}
          columnsData={schoolSpeakersColumnsData}
        />
      </DataSection>
      <DataSection titleText="Чему вы научитесь">
        <SubjectionSection
          columnTitles={['Наименование', 'Описание']}
          columnsData={schoolAbilitiesColumnsData}
        />
      </DataSection>
      <div className={styles['buttons-wrapper']}>
        <Button
          onClick={goBack}
          type="dashed"
          size="large"
          className={styles.button}
        >
          Назад
        </Button>
        <Button
          type="primary"
          onClick={openUnpublishSchoolModal}
          className={styles.button}
        >
          Снять с публикации
        </Button>
      </div>
      <UnpublishSchoolModal
        isModalOpen={isUnpublishSchoolModalOpened}
        handleCancel={closeUnpublishSchoolModal}
        schoolInfo={{
          id: schoolId,
          name: schoolName,
        }}
      />
    </>
  );
};
